import {
  CreateOrderResponse,
  DataProps,
  DeliveryOrdersResponse,
  Order,
  OrderHistory,
  OrderHistoryParams,
} from '@diamond/shared/types';
import { apiInstance, buyerCaptureException } from '@diamond/shared/utils';
import { AxiosError, isAxiosError } from 'axios';
import cleanDeep from 'clean-deep';

export class DeliveryOrdersRequestError extends Error {
  statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = 'DeliveryOrdersRequestError';
    this.statusCode = statusCode;
  }
}

export const getOrderHistory = async (
  token: string,
  payload: OrderHistoryParams
) => {
  try {
    const { data } = await apiInstance(token).get<OrderHistory>(
      `/order/histories`,
      {
        params: cleanDeep(payload),
      }
    );
    return data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError;
      buyerCaptureException(
        axiosError.response?.data as DataProps,
        (scope, user) => {
          scope.setTag('OrderHistory', `metaId ${payload['warehouse']}`);
          scope.setUser({
            ...(user ?? {}),
            additional: {
              warehouse: payload['warehouse'],
            },
          });
          scope.setLevel('error');
        }
      );
    }
  }
  throw new Error('Error Get OrderHistory');
};

export const getOrderDetail = async (
  token: string,
  metaId: string,
  warehouse: string
): Promise<Order> => {
  try {
    const { data } = await apiInstance(token).get<Order>(
      `/order/meta/${metaId}?warehouse=${warehouse}`
    );
    return data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError;
      buyerCaptureException(
        axiosError.response?.data as DataProps,
        (scope, user) => {
          scope.setTag('OrderDetail', `metaId ${metaId}`);
          scope.setUser({
            ...(user ?? {}),
            additional: {
              warehouse,
            },
          });
          scope.setLevel('error');
        }
      );
    }
  }
  throw new Error('Error Get Orderdetail');
};

export const createOrder = async (
  token: string,
  payload: {
    warehouse_id: string;
    products: Array<{ id: string; quantity: number; note: string | null }>;
    voucher_codes?: string[];
  }
) => {
  try {
    const url = `/order`;
    const { data } = await apiInstance(token).post<CreateOrderResponse>(
      url,
      payload
    );
    return data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const errorData = error.response?.data as DataProps;
      buyerCaptureException(errorData, (scope) => {
        scope.setTags({
          queryKey: 'createOrder error',
          status_code: (errorData as { statusCode?: number })?.statusCode,
        });
        scope.setContext('error', {
          error: errorData,
        });
        scope.setExtra('warehouse_id', payload.warehouse_id);
        scope.setExtra('products_count', payload.products.length);
        scope.setLevel('error');
      });
      throw new Error(
        (errorData as { message?: string })?.message || 'Failed to create order'
      );
    }
    throw error;
  }
};

// ==================== Delivery Orders (Multiple DO Handling) ====================

/**
 * Get delivery orders for an order meta
 * API Endpoint: GET /api/v1/order/meta/{orderMetaId}/delivery-orders
 */
export const getDeliveryOrders = async (
  token: string,
  orderMetaId: string,
  warehouse: string
): Promise<DeliveryOrdersResponse> => {
  try {
    const { data } = await apiInstance(token).get<DeliveryOrdersResponse>(
      `/order/meta/${orderMetaId}/delivery-orders`,
      { params: { warehouse } }
    );
    return data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError;
      const responseData = axiosError.response?.data as DataProps | undefined;
      buyerCaptureException(
        responseData,
        (scope, user) => {
          scope.setTag('DeliveryOrders', `orderMetaId ${orderMetaId}`);
          scope.setUser({
            ...(user ?? {}),
            additional: { orderMetaId, warehouse },
          });
          scope.setLevel('error');
        }
      );
      throw new DeliveryOrdersRequestError(
        (responseData as { message?: string })?.message ??
          'Error Get DeliveryOrders',
        axiosError.response?.status
      );
    }
    throw new DeliveryOrdersRequestError('Error Get DeliveryOrders');
  }
};
